// src/Pages/Dashboard/widgets/ProjectView/Tabs/Modal/ProjectReportModal.jsx
import React, { useEffect } from "react";
import { FaTimes, FaCalendarAlt, FaFilePdf } from "react-icons/fa";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
// ⬅️ Reuse the same CSS as other report modals or point to a new file
import "../../../../../Stylesheets/Dashboard/Tabs/Expenses/ViewReportModal.css";

const toISODate = (d) => {
  if (!d || Number.isNaN(d.getTime())) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const fromISODate = (s) => {
  if (!s) return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
};

const ProjectReportModal = ({
  show,
  onClose,
  startDate,        // string: "YYYY-MM-DD"
  endDate,          // string: "YYYY-MM-DD"
  setStartDate,     // fn(next: string)
  setEndDate,       // fn(next: string)
  detail,           // "summary" | "detailed"
  setDetail,        // fn(next: "summary" | "detailed")
  onOpenPreview,    // fn()
}) => {
  useEffect(() => {
    if (!show) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [show, onClose]);

  if (!show) return null;

  const startObj = fromISODate(startDate);
  const endObj = fromISODate(endDate);

  const applyPreset = (days) => {
    const to = new Date();
    const from = new Date();
    from.setDate(to.getDate() - days);
    setStartDate(toISODate(from));
    setEndDate(toISODate(to));
  };

  const applyThisMonth = () => {
    const now = new Date();
    const from = new Date(now.getFullYear(), now.getMonth(), 1);
    const to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    setStartDate(toISODate(from));
    setEndDate(toISODate(to));
  };

  const primaryDisabled = !startDate || !endDate;

  const handlePrimary = () => {
    if (primaryDisabled) return;
    onOpenPreview && onOpenPreview();
  };

  return (
    <div
      className="report-modal-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="report-modal report-modal-anim">
        {/* HEADER */}
        <div className="report-modal-header">
          <div>
            <h2 className="report-modal-title">Project Report</h2>
            <p className="report-modal-subtitle">
              Generate an overall report combining Labor, Materials and Expenses.
            </p>
          </div>
          <button
            className="report-close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </div>

        {/* BODY */}
        <div className="report-modal-body">
          {/* DATE RANGE */}
          <div className="report-section">
            <label className="report-label">Date Range</label>

            <div className="date-presets">
              <button type="button" onClick={() => applyPreset(7)}>
                Last 7 days
              </button>
              <button type="button" onClick={() => applyPreset(30)}>
                Last 30 days
              </button>
              <button type="button" onClick={applyThisMonth}>
                This month
              </button>
            </div>

            <div className="date-range-grid">
              <div className="date-picker-wrapper">
                <FaCalendarAlt className="date-icon" />
                <DatePicker
                  selected={startObj}
                  onChange={(d) => setStartDate(toISODate(d))}
                  placeholderText="Start date"
                  dateFormat="yyyy-MM-dd"
                  className="report-input"
                  maxDate={endObj || new Date()}
                />
              </div>

              <div className="date-picker-wrapper">
                <FaCalendarAlt className="date-icon" />
                <DatePicker
                  selected={endObj}
                  onChange={(d) => setEndDate(toISODate(d))}
                  placeholderText="End date"
                  dateFormat="yyyy-MM-dd"
                  className="report-input"
                  minDate={startObj}
                  maxDate={new Date()}
                />
              </div>
            </div>
          </div>

          {/* DETAIL LEVEL */}
          <div className="report-section">
            <label className="report-label">Detail Level</label>
            <div className="format-toggle-advanced">
              <button
                type="button"
                className={`format-pill ${
                  detail === "summary" ? "active" : ""
                }`}
                onClick={() => setDetail("summary")}
              >
                Summary
              </button>
              <button
                type="button"
                className={`format-pill ${
                  detail === "detailed" ? "active" : ""
                }`}
                onClick={() => setDetail("detailed")}
              >
                Detailed
              </button>
            </div>
            <div className="format-hint">
              Summary = high-level metrics per module. Detailed = deeper
              breakdown across Labor, Materials & Expenses.
            </div>
          </div>
        </div>

        {/* FOOTER */}
        <div className="report-modal-footer">
          <button className="btn-ghost" onClick={onClose}>
            Close
          </button>

          <button
            className="btn-primary"
            onClick={handlePrimary}
            disabled={primaryDisabled}
          >
            <FaFilePdf style={{ marginRight: 6 }} />
            Open Preview
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProjectReportModal;
